import React, { useState, useCallback } from 'react';
import {
    View,
    Text,
    StyleSheet,
    ScrollView,
    RefreshControl,
    ActivityIndicator,
    StatusBar,
    Platform,
} from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import axios from 'axios';
import { COLORS, API } from '../config';
import { useAuth } from '../context/AuthContext';

export default function RiwayatScreen() {
    const { user } = useAuth();
    const [allTransactions, setAllTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    const fetchData = async () => {
        try {
            // Fetch all with error handling for each
            const results = await Promise.allSettled([
                axios.get(API.MEMBER.RIWAYAT_POIN(user.id)),
                axios.get(API.MEMBER.RIWAYAT_MAIN(user.id)),
                axios.get(API.MEMBER.RIWAYAT_REDEEM(user.id)),
                axios.get(API.TOPUP.HISTORY(user.id)),
            ]);

            let transactions = [];

            // Poin history
            if (results[0].status === 'fulfilled' && results[0].value?.data?.success) {
                const data = results[0].value.data.data || [];
                data.forEach(item => {
                    transactions.push({
                        type: item.jenis === 'masuk' ? 'poin_in' : 'poin_out',
                        title: item.keterangan || (item.jenis === 'masuk' ? 'Dapat Poin' : 'Pakai Poin'),
                        amount: item.jumlah || 0,
                        date: item.tanggal,
                        icon: item.jenis === 'masuk' ? '🎮' : '🎁',
                    });
                });
            }

            // Play/transaction history
            if (results[1].status === 'fulfilled' && results[1].value?.data?.success) {
                const data = results[1].value.data.data || [];
                data.forEach(item => {
                    // Check if it's a saldo transaction (MG payment)
                    if (item.metode_bayar === 'saldo') {
                        transactions.push({
                            type: 'saldo_usage',
                            title: item.keterangan || 'Pembayaran dengan MG',
                            amount: item.nominal_bayar || 0,
                            date: item.tanggal,
                            icon: '💳',
                        });
                    } else {
                        // Cash transaction - earns poin
                        transactions.push({
                            type: 'transaksi',
                            title: item.keterangan || item.console || 'Transaksi',
                            amount: item.poin_didapat || 0,
                            date: item.tanggal,
                            icon: '⚽',
                        });
                    }
                });
            }

            // Redeem history
            if (results[2].status === 'fulfilled' && results[2].value?.data?.success) {
                const data = results[2].value.data.data || [];
                data.forEach(item => {
                    let statusText = 'Menunggu';
                    if (item.status === 'completed') statusText = 'Berhasil Klaim';
                    else if (item.status === 'rejected') statusText = 'Ditolak';

                    transactions.push({
                        type: 'redeem',
                        title: `Tukar: ${item.hadiah_nama || 'Hadiah'}`,
                        subtitle: statusText,
                        amount: item.poin_digunakan || 0,
                        date: item.tanggal,
                        icon: '🎁',
                        status: item.status,
                    });
                });
            }

            // Topup history
            if (results[3].status === 'fulfilled' && results[3].value?.data?.success) {
                const data = results[3].value.data.data || [];
                data.forEach(item => {
                    let statusText = 'Menunggu';
                    if (item.status === 'approved') statusText = 'Berhasil';
                    else if (item.status === 'rejected') statusText = 'Ditolak';

                    transactions.push({
                        type: 'topup',
                        title: 'Topup Minguro',
                        subtitle: statusText,
                        amount: item.jumlah || 0,
                        date: item.created_at || item.tanggal,
                        icon: '💰',
                        status: item.status,
                    });
                });
            }

            // Sort by date descending, filter out invalid dates
            transactions = transactions.filter(t => t.date);
            transactions.sort((a, b) => new Date(b.date) - new Date(a.date));

            setAllTransactions(transactions);
        } catch (error) {
            console.error('Fetch error:', error);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useFocusEffect(
        useCallback(() => {
            fetchData();
        }, [])
    );

    const onRefresh = () => {
        setRefreshing(true);
        fetchData();
    };

    const formatNumber = (num) => {
        if (!num && num !== 0) return '0';
        return Math.floor(num).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        try {
            const date = new Date(dateStr);
            if (isNaN(date.getTime())) return '';
            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'];
            const hours = date.getHours().toString().padStart(2, '0');
            const mins = date.getMinutes().toString().padStart(2, '0');
            return `${date.getDate()} ${months[date.getMonth()]} • ${hours}:${mins}`;
        } catch (e) {
            return '';
        }
    };

    const getAmountStyle = (type) => {
        if (type === 'poin_in' || type === 'transaksi') return { color: COLORS.success, prefix: '+' };
        if (type === 'poin_out' || type === 'redeem') return { color: COLORS.danger, prefix: '-' };
        if (type === 'topup') return { color: COLORS.primary, prefix: '+MG ' };
        if (type === 'saldo_usage') return { color: COLORS.warning, prefix: '-MG ' };
        return { color: COLORS.textPrimary, prefix: '' };
    };

    const getStatusBadge = (status) => {
        if (status === 'completed' || status === 'approved') {
            return { bg: COLORS.success + '15', color: COLORS.success };
        }
        if (status === 'rejected') {
            return { bg: COLORS.danger + '15', color: COLORS.danger };
        }
        return { bg: COLORS.warning + '15', color: COLORS.warning };
    };

    if (loading) {
        return (
            <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color={COLORS.primary} />
            </View>
        );
    }

    return (
        <View style={styles.container}>
            <StatusBar barStyle="dark-content" backgroundColor={COLORS.bgDarker} />

            <View style={styles.header}>
                <Text style={styles.headerTitle}>Riwayat</Text>
                <Text style={styles.headerSubtitle}>Semua aktivitas akun kamu</Text>
            </View>

            <ScrollView
                style={styles.scrollView}
                showsVerticalScrollIndicator={false}
                refreshControl={
                    <RefreshControl
                        refreshing={refreshing}
                        onRefresh={onRefresh}
                        tintColor={COLORS.primary}
                    />
                }
            >
                {allTransactions.length === 0 ? (
                    <View style={styles.emptyState}>
                        <Text style={styles.emptyIcon}>📭</Text>
                        <Text style={styles.emptyTitle}>Belum Ada Aktivitas</Text>
                        <Text style={styles.emptyText}>Riwayat transaksi akan muncul di sini</Text>
                    </View>
                ) : (
                    allTransactions.map((item, index) => {
                        const amountStyle = getAmountStyle(item.type);
                        const statusBadge = item.status ? getStatusBadge(item.status) : null;

                        return (
                            <View key={index} style={styles.transactionCard}>
                                <View style={styles.transactionContent}>
                                    {/* Type Badge */}
                                    <View style={[styles.typeBadge, {
                                        backgroundColor: item.type === 'poin_in' || item.type === 'transaksi'
                                            ? COLORS.success + '20'
                                            : item.type === 'topup'
                                                ? COLORS.primary + '20'
                                                : COLORS.warning + '20'
                                    }]}>
                                        <Text style={[styles.typeText, {
                                            color: item.type === 'poin_in' || item.type === 'transaksi'
                                                ? COLORS.success
                                                : item.type === 'topup'
                                                    ? COLORS.primary
                                                    : COLORS.warning
                                        }]}>
                                            {item.type === 'poin_in' ? 'Poin Masuk'
                                                : item.type === 'poin_out' ? 'Poin Keluar'
                                                    : item.type === 'transaksi' ? 'Dapat Poin'
                                                        : item.type === 'topup' ? 'Topup Saldo'
                                                            : item.type === 'saldo_usage' ? 'Pakai Saldo'
                                                                : 'Tukar Hadiah'}
                                        </Text>
                                    </View>
                                    <Text style={styles.transactionTitle}>{item.title}</Text>
                                    <View style={styles.transactionMeta}>
                                        <Text style={styles.transactionDate}>{formatDate(item.date)}</Text>
                                        {item.subtitle && statusBadge && (
                                            <View style={[styles.statusBadge, { backgroundColor: statusBadge.bg }]}>
                                                <Text style={[styles.statusText, { color: statusBadge.color }]}>{item.subtitle}</Text>
                                            </View>
                                        )}
                                    </View>
                                </View>
                                <View style={styles.amountContainer}>
                                    <Text style={[styles.transactionAmount, { color: amountStyle.color }]}>
                                        {amountStyle.prefix}{formatNumber(item.amount)}
                                    </Text>
                                    <Text style={styles.amountUnit}>
                                        {(item.type === 'topup' || item.type === 'saldo_usage') ? 'MG' : 'MP'}
                                    </Text>
                                </View>
                            </View>
                        );
                    })
                )}

                <View style={{ height: 100 }} />
            </ScrollView>
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: COLORS.bgDark,
    },
    loadingContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: COLORS.bgDark,
    },
    header: {
        paddingTop: Platform.OS === 'web' ? 20 : 50,
        paddingBottom: 16,
        paddingHorizontal: 20,
        backgroundColor: COLORS.bgDarker,
    },
    headerTitle: {
        fontSize: 22,
        fontWeight: '700',
        color: COLORS.textPrimary,
    },
    headerSubtitle: {
        fontSize: 13,
        color: COLORS.textSecondary,
        marginTop: 4,
    },
    scrollView: {
        flex: 1,
        paddingHorizontal: 10,
        paddingTop: 12,
    },
    transactionCard: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: COLORS.bgCard,
        borderRadius: 14,
        padding: 14,
        marginBottom: 10,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.05,
        shadowRadius: 8,
        elevation: 2,
    },
    transactionIcon: {
        width: 46,
        height: 46,
        borderRadius: 12,
        backgroundColor: COLORS.bgDark,
        justifyContent: 'center',
        alignItems: 'center',
        marginRight: 12,
    },
    iconText: {
        fontSize: 20,
    },
    transactionContent: {
        flex: 1,
    },
    transactionTitle: {
        color: COLORS.textPrimary,
        fontWeight: '600',
        fontSize: 14,
        marginBottom: 4,
    },
    transactionMeta: {
        flexDirection: 'row',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 8,
    },
    transactionDate: {
        color: COLORS.textMuted,
        fontSize: 11,
    },
    statusBadge: {
        paddingHorizontal: 8,
        paddingVertical: 2,
        borderRadius: 6,
    },
    statusText: {
        fontSize: 9,
        fontWeight: '600',
    },
    transactionAmount: {
        fontSize: 14,
        fontWeight: '700',
    },
    emptyState: {
        alignItems: 'center',
        padding: 50,
        backgroundColor: COLORS.bgCard,
        borderRadius: 16,
    },
    emptyIcon: {
        fontSize: 48,
        marginBottom: 12,
        opacity: 0.5,
    },
    emptyTitle: {
        fontSize: 16,
        fontWeight: '600',
        color: COLORS.textPrimary,
        marginBottom: 4,
    },
    emptyText: {
        color: COLORS.textSecondary,
        fontSize: 13,
    },
    typeBadge: {
        alignSelf: 'flex-start',
        paddingHorizontal: 8,
        paddingVertical: 3,
        borderRadius: 6,
        marginBottom: 4,
    },
    typeText: {
        fontSize: 10,
        fontWeight: '700',
    },
    amountContainer: {
        alignItems: 'flex-end',
    },
    amountUnit: {
        fontSize: 10,
        color: COLORS.textMuted,
        marginTop: 2,
    },
});
